import { useRef, useState } from 'react'
import styled from 'styled-components'
import DatePopover from './DatePopover'
import OwnerPicker from './OwnerPicker'
import { canRejectAiFind } from './remedialMeta'
import { ACCENT, BORDER, INK, PrimaryButton, SecondaryButton, TextButton } from './ui'

const BulkBar = ({ ids, actions, onClear, onConvert, onDismiss, onOwner, onDue }) => {
  const ownerRef = useRef(null)
  const dueRef = useRef(null)
  const [ownerOpen, setOwnerOpen] = useState(false)
  const [dueOpen, setDueOpen] = useState(false)

  if (!ids.length) return null

  const chosen = actions.filter(action => ids.includes(action.id))
  const buildings = [...new Set(chosen.map(action => action.buildingPassportId))]
  const oneBuilding = buildings.length === 1 ? buildings[0] : null
  const noun = `${ids.length} remedial${ids.length === 1 ? '' : 's'}`
  const convertible = chosen.filter(action => action.permissions?.canConvert)
  const allRejectable = chosen.length > 0 && chosen.length === ids.length && chosen.every(canRejectAiFind)

  return (
    <Bar role='toolbar' aria-label='Change the selected remedials'>
      <Left>
        <Count>{ids.length} selected</Count>
        <TextButton type='button' onClick={onClear}>Clear selection</TextButton>
      </Left>
      <Buttons>
        <PrimaryButton
          type='button'
          onClick={onConvert}
          disabled={!convertible.length}
          title={convertible.length ? undefined : 'None of the selected remedials can have a task created for them'}
        >
          {convertible.length ? `Create tasks (${convertible.length})` : 'Create tasks'}
        </PrimaryButton>
        <SecondaryButton ref={ownerRef} type='button' onClick={() => setOwnerOpen(true)}>Change owner</SecondaryButton>
        <SecondaryButton ref={dueRef} type='button' onClick={() => setDueOpen(true)}>Change due date</SecondaryButton>
        {allRejectable && (
          <SecondaryButton
            type='button'
            onClick={onDismiss}
            title='Remove these AI finds because they are not real issues'
          >
            Not a real issue
          </SecondaryButton>
        )}
      </Buttons>
      <OwnerPicker
        anchorEl={ownerRef.current}
        open={ownerOpen}
        onClose={() => setOwnerOpen(false)}
        buildingPassportId={oneBuilding}
        title={`Choose the owner for ${noun}`}
        onSelect={person => {
          setOwnerOpen(false)
          if (person) onOwner(person)
        }}
      />
      <DatePopover
        anchorEl={dueRef.current}
        open={dueOpen}
        onClose={() => setDueOpen(false)}
        title='Change due date'
        helper={`Sets the same due date on ${noun}. Any that already have a task keep following their task deadline and are skipped.`}
        saveLabel={`Apply to ${noun}`}
        onSave={date => {
          setDueOpen(false)
          onDue(date)
        }}
      />
    </Bar>
  )
}

export default BulkBar

const Bar = styled.div`
  position: sticky;
  bottom: 16px;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-left: 3px solid ${ACCENT};
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(12, 18, 41, 0.08);
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Count = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
`

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  @media (max-width: 768px) {
    width: 100%;
    & > button { flex: 1 1 45%; }
  }
`
